import React, { Component } from "react";
import { debounce } from "lodash";
import { Menu, Dropdown, Icon, message } from "antd";
import { connect } from "react-redux";
import Api from "../../helpers/apiHandler";
import { loginUserType } from "../../helpers/constants";
import { CURRENT_BUSINESS_LOCATION_STORAGE_KEY } from "../../helpers/businessLocation";

const api = new Api();

const COMMON_SEARCH_DEBOUNCE_MS = 600;

function createDebouncedCommonSearchPost(executor) {
  let pending = null;
  const debouncedInvoke = debounce((data, resolve, reject) => {
    executor(data).then(resolve).catch(reject);
  }, COMMON_SEARCH_DEBOUNCE_MS);

  return (data) => {
    const trimmed = String(data?.query || "").trim();
    if (!trimmed) {
      return executor(data);
    }
    return new Promise((resolve, reject) => {
      pending = { resolve, reject };
      debouncedInvoke(data, resolve, reject);
    });
  };
}

const postCommonSearch = createDebouncedCommonSearchPost((data) =>
  api.post("common/search", {
    isMultipart: false,
    data,
  })
);

// TZ: broadcast when locations list changes so asset/audit screens refresh header TZ
export const BUSINESS_LOCATIONS_UPDATED_EVENT = "businessLocationsUpdated";

export function notifyBusinessLocationsUpdated(deletedLocationId) {
  const detail =
    deletedLocationId != null && Number(deletedLocationId) > 0
      ? { deletedLocationId: Number(deletedLocationId) }
      : {};
  window.dispatchEvent(
    new CustomEvent(BUSINESS_LOCATIONS_UPDATED_EVENT, { detail })
  );
}

function persistCurrentBusinessLocationId(locationId) {
  const id = Number(locationId);
  if (!Number.isFinite(id) || id <= 0) return;
  try {
    window.localStorage.setItem(CURRENT_BUSINESS_LOCATION_STORAGE_KEY, String(id));
  } catch (e) {
    // ignore quota / private mode
  }
}

/** Normalize common/search or list rows to { id, name, isDefaultLocation }. */
function normalizeBusinessLocationRow(item) {
  if (!item || typeof item !== "object") return null;
  const rawId = item.id != null ? item.id : item.companyProductionUnitId;
  const id = Number(rawId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const name =
    item.name != null && String(item.name).trim() !== ""
      ? String(item.name)
      : item.label != null && String(item.label).trim() !== ""
        ? String(item.label)
        : `Location ${id}`;
  return {
    id,
    name,
    isDefaultLocation: Boolean(item.isDefaultLocation),
  };
}

  function canFetchBusinessLocations(props) {
  const companyId = Number(props.companyId);
  if (!Number.isFinite(companyId) || companyId <= 0) {
    return false;
  }

  if (props.userType === loginUserType.employee) {
    const employeeId = Number(props.employeeId);
    return Number.isFinite(employeeId) && employeeId > 0;
  }

  return true;
}

class LocationDropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      businessLocations: [],
      selectedLocation: null,
    };
  }

  componentDidMount() {
    window.addEventListener("locationChanged", this.handleExternalLocationChange);
    window.addEventListener(
      BUSINESS_LOCATIONS_UPDATED_EVENT,
      this.handleBusinessLocationsUpdated
    );
    if (canFetchBusinessLocations(this.props)) {
      this.fetchBusinessLocations();
    }
  }

  componentDidUpdate(prevProps) {
    const companyIdReady =
      Number(this.props.companyId) > 0 &&
      Number(this.props.companyId) !== Number(prevProps.companyId);
    const employeeContextChanged =
      this.props.userType === loginUserType.employee &&
      (prevProps.employeeId !== this.props.employeeId ||
        prevProps.userType !== this.props.userType);
    const becameReady =
      canFetchBusinessLocations(this.props) &&
      !canFetchBusinessLocations(prevProps);

    if (companyIdReady || employeeContextChanged || becameReady) {
      this.fetchBusinessLocations();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("locationChanged", this.handleExternalLocationChange);
    window.removeEventListener(
      BUSINESS_LOCATIONS_UPDATED_EVENT,
      this.handleBusinessLocationsUpdated
    );
  }

  handleBusinessLocationsUpdated = () => {
    if (canFetchBusinessLocations(this.props)) {
      this.fetchBusinessLocations();
    }
  };

  emitLocationChanged = (locationId) => {
    const locationChangeEvent = new CustomEvent("locationChanged", {
      detail: { locationId: Number(locationId) },
    });
    window.dispatchEvent(locationChangeEvent);
  };

  handleExternalLocationChange = (event) => {
    const locationId = event && event.detail ? Number(event.detail.locationId) : null;
    if (!locationId) return;

    const selectedLocation = this.state.businessLocations.find(
      (location) => Number(location.id) === locationId
    );

    if (selectedLocation) {
      this.setState({ selectedLocation });
      persistCurrentBusinessLocationId(locationId);
      return;
    }

    // If the selected location isn't in the local menu list, refresh once.
    if (canFetchBusinessLocations(this.props)) {
      this.fetchBusinessLocations();
    }
  };

  fetchBusinessLocations = () => {
    if (!canFetchBusinessLocations(this.props)) {
      return;
    }

    const companyId = Number(this.props.companyId);

    if (this.props.userType === loginUserType.employee) {
      const employeeId = Number(this.props.employeeId);
      if (!Number.isFinite(employeeId) || employeeId <= 0) {
        return;
      }
      postCommonSearch({
        companyId,
        type: "empBusinessLocation",
        employeeId,
        query: "",
      })
        .then((response) => {
          if (response.status !== 1) {
            throw new Error("Failed to fetch employee business locations");
          }
          const rawList = (response.data && response.data.list) || [];
          const uniqueLocations = rawList
            .map(normalizeBusinessLocationRow)
            .filter(Boolean)
            .filter(
              (location, index, arr) =>
                arr.findIndex((item) => Number(item.id) === Number(location.id)) === index
            );
          const defaultLocation = uniqueLocations.find((loc) => loc.isDefaultLocation);
          const selectedLocation =
            defaultLocation || (uniqueLocations.length > 0 ? uniqueLocations[0] : null);

          this.setState({
            businessLocations: uniqueLocations,
            selectedLocation,
          });

          if (selectedLocation) {
            persistCurrentBusinessLocationId(selectedLocation.id);
            this.emitLocationChanged(selectedLocation.id);
          }
        })
        .catch((error) => {
          console.error("Error fetching business locations:", error);
        });
      return;
    }

    const pageSize = 100;
    let pageId = 0;
    let hasMore = true;
    let allLocations = [];

    // TZ: paginate all locations — single page missed sites on companies with 100+ locations
    const fetchAllPages = async () => {
      while (hasMore) {
        const parameters = {
          page: { limit: pageSize, pageId },
          sortBy: { property: "name", direction: "ASC" },
          query: "",
          companyId,
        };

        const response = await api.post("companyProductionUnit/list", {
          isMultipart: false,
          data: parameters,
        });

        if (response.status !== 1) {
          throw new Error("Failed to fetch business locations");
        }

        const list = (response.data && response.data.list) || [];
        allLocations = allLocations.concat(list);

        if (list.length < pageSize) {
          hasMore = false;
        } else {
          pageId += 1;
        }
      }
    };

    fetchAllPages()
      .then(() => {
        const uniqueLocations = allLocations
          .map(normalizeBusinessLocationRow)
          .filter(Boolean)
          .filter(
            (location, index, arr) =>
              arr.findIndex((item) => Number(item.id) === Number(location.id)) === index
          );

        const previousSelectedId = this.state.selectedLocation
          ? Number(this.state.selectedLocation.id)
          : null;
        let selectedLocation = previousSelectedId
          ? uniqueLocations.find((loc) => Number(loc.id) === previousSelectedId) || null
          : null;

        if (!selectedLocation) {
          const defaultLocation = uniqueLocations.find(
            (location) => location.isDefaultLocation === true
          );
          if (defaultLocation) {
            selectedLocation = defaultLocation;
          }
        }

        if (!selectedLocation) {
          try {
            const storedId = Number(
              window.localStorage.getItem(CURRENT_BUSINESS_LOCATION_STORAGE_KEY)
            );
            if (Number.isFinite(storedId) && storedId > 0) {
              selectedLocation =
                uniqueLocations.find((loc) => Number(loc.id) === storedId) || null;
            }
          } catch (e) {
            // ignore quota / private mode
          }
        }

        if (!selectedLocation) {
          selectedLocation = uniqueLocations.length > 0 ? uniqueLocations[0] : null;
        }

        const nextSelectedId = selectedLocation ? Number(selectedLocation.id) : null;

        this.setState({
          businessLocations: uniqueLocations,
          selectedLocation: selectedLocation,
        });

        if (selectedLocation) {
          persistCurrentBusinessLocationId(selectedLocation.id);
          if (nextSelectedId !== previousSelectedId) {
            this.emitLocationChanged(selectedLocation.id);
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching business locations:", error);
      });
  };

  handleLocationChange = (locationId) => {
    const { businessLocations } = this.state;
    const companyId = Number(this.props.companyId);

    const selectedLocation = businessLocations.find(
      (location) => location.id === Number(locationId)
    );

    if (this.props.userType === loginUserType.employee) {
      if (!selectedLocation) return;
      const updatedLocationId = Number(locationId);
      this.setState({ selectedLocation });
      persistCurrentBusinessLocationId(updatedLocationId);
      this.emitLocationChanged(updatedLocationId);
      return;
    }

    api
      .post("companyProductionUnit/updateDefaultLocation", {
        isMultipart: false,
        data: {
          companyId: companyId,
          id: Number(locationId),
        },
      })
      .then((response) => {
        if (response.status === 1) {
          const updatedLocationId =
            response.data && response.data.id ? response.data.id : Number(locationId);
          this.setState({ selectedLocation });
          persistCurrentBusinessLocationId(updatedLocationId);
          message.success(response.message || "Default location updated");

          this.emitLocationChanged(updatedLocationId);
        } else {
          message.error(
            response.message || "Failed to update default location"
          );
        }
      })
      .catch((error) => {
        console.error("Error updating default location:", error);
        message.error("Error updating default location");
      });
  };

  render() {
    const { businessLocations, selectedLocation } = this.state;

    if (businessLocations.length === 0) {
      return null;
    }

    const label = selectedLocation
      ? selectedLocation.name
      : "Select Business Location";

    if (businessLocations.length === 1) {
      return (
        <div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "400",
            }}
          >
            {label}
          </span>
        </div>
      );
    }

    const menu = (
      <Menu
        onClick={({ key }) => this.handleLocationChange(key)}
        style={{ maxHeight: 320, overflowY: "auto", overflowX: "hidden" }}
      >
        {businessLocations.map((location) => (
          <Menu.Item key={String(location.id)}>{location.name}</Menu.Item>
        ))}
      </Menu>
    );

    return (
      <div>
        <Dropdown overlay={menu} trigger={["click"]} overlayStyle={{ minWidth: 260 }}>
          <span
            style={{
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "400",
            }}
          >
            <span style={{ marginRight: 6 }}>{label}</span>
            <Icon type="down" style={{ fontSize: "12px" }} />
          </span>
        </Dropdown>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  companyId: state.Auth.company && state.Auth.company.id ? Number(state.Auth.company.id) : 0,
  employeeId: state.Auth.id != null ? Number(state.Auth.id) : 0,
  userType: state.Auth.type || "",
});

export default connect(mapStateToProps)(LocationDropdown);
